import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link, Routes, Route, NavLink, useParams, useLocation } from 'react-router-dom';

// Third-party packages.
import Helmet from 'react-helmet';

// Helpers.
import Helper from "../../helpers/Helper";
import { getFilterParameters } from "../../helpers/utils";

// Contexts.
import { TimeRangeContext } from "../../context/TimeRangeContext";
import { FilterContext } from "../../context/FilterContext";

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import EventType from '../../types/instance/EventType';
import HostTarget from '../../types/host/HostTarget';
import ExecutionPlan from '../../types/instance/ExecutionPlan';
import ChartPlotLine from "../../types/ChartPlotLine";

// Constants
import { INSTANCE_TYPE_SQLSERVER } from "../../component/Constants";

// Components.
import PeriodBreadcrumb from '../../component/PeriodBreadcrumb';
import Breadcrumb from "../../container/breadcrumb";
import HeaderActions from "../../container/header_actions";
import ConditionalRender from "../../helpers/ConditionalRender";
import FilterBar from "../../component/FilterBar";
import TabbedMenu from "../../container/tabbed-menu";
import TextAndPlan from "./tabs/TextAndPlan";
import StatementActivity from "./tabs/Activity";
import SqlStatistics from "./tabs/SQLStatistics";
import Batches from "../../component/instance/Tabs/Batches";
import Events from "./tabs/Events";

interface IStatementDetailProps {
    instances: InstanceTarget[],
    hosts: HostTarget[],
    eventTypes: EventType[],
    toggleMenu: Function,
    applyPeriod: Function,
    setFilterOptions: Function
}

const SQLStatisticStatement = (props: IStatementDetailProps) => {
    const { instances } = props;
    const { instanceId, batchId, statementId, batchStatementId } = useParams();
    const getInstance: InstanceTarget[] = instances.filter(instance => instance.id === Number(instanceId))
    const instance = getInstance[0]
    const [chartPlanPlotLines, setChartPlanPlotLines] = useState<ChartPlotLine[]>([]);
    const location = useLocation();
    const timeRangeContext = useContext(TimeRangeContext);
    const { currentFilterOptions } = useContext(FilterContext);
    const filterParameters = getFilterParameters(currentFilterOptions);

    if (instance === undefined) {
        return (
                <React.Fragment>No Instance</React.Fragment>
        )
    }

    const setExecutionPlanOptions = (executionPlans: ExecutionPlan[]) => {
        const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange.from, timeRangeContext.timeRange.to, timeRangeContext.timeRange.interval);
        setChartPlanPlotLines(Helper.getChartPlotLinesForExecutionPlans(timeRangeContext.timeRange, executionPlans, chartCategories))

    }

    const shouldShowTab = (value: string): boolean => {
        const currentInstance: any = instance
        return currentInstance !== undefined && (!!(currentInstance[`has${value}`] && currentInstance[`collect${value}`]))
    }

    const getURL = (newSegment: string) => {
        const segments = location.pathname.split('/');
        segments[segments.length - 1] = newSegment;
        return segments.join('/');
    };


    return (
            <div id="content">
                <Helmet>
                    <title>{instance.name} - Statement View - DBmarlin</title>
                    <meta name="description"
                          content="Instance statement performance view, showing key performance metrics and detailed executed statement and waits information"/>
                </Helmet>

                {/* Heading */}
                <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                    <Breadcrumb instanceId={instance.id}
                                type="instances"
                                heading={instance.name}>
                        <Link to="/instances">Analysis</Link>
                        <Link to="/instances">Instances</Link>
                        <Link to={`/instances/${instanceId}/activity`}>{instance.name}</Link>
                        <Link
                                to={`/instances/${instanceId}/sql-statistics`}>SQL Statistics</Link>

                        <NavLink to={`/instances/`}
                                 className="current-breadcrumb-link pe-none">Statement {statementId}</NavLink>
                        <PeriodBreadcrumb/>
                    </Breadcrumb>

                   <HeaderActions toggleMenu={props.toggleMenu} refresh={() => {
                    }}
                                   applyPeriod={props.applyPeriod}/>
                </div>


                <div className="loader"/>
                <TabbedMenu>
                    {/*<TimeShift/>*/}

                    {/*Statement and Plans*/}
                    <NavLink to={getURL('text')} className={({ isActive }) => (isActive ? 'active' : '')}>Statement</NavLink>

                    {/*Activity*/}
                    <NavLink to={getURL('activity')} className={({ isActive }) => (isActive ? 'active' : '')}>Statement Activity</NavLink>

                    <ConditionalRender if={shouldShowTab('sqlstatistics')}>
                        <NavLink
                                to={getURL('sql-statistics')}
                                className={({ isActive }) => (isActive ? 'active' : '')}>SQL Statistics</NavLink>
                    </ConditionalRender>

                    <ConditionalRender if={instance?.type === INSTANCE_TYPE_SQLSERVER}>
                        <NavLink
                                to={getURL('batches')}
                                className={({ isActive }) => (isActive ? 'active' : '')}>Calling Batches</NavLink>
                    </ConditionalRender>

                    <NavLink to={getURL('events')} className={({ isActive }) => (isActive ? 'active' : '')}>
                        Events
                    </NavLink>
                </TabbedMenu>

                {/* Filters */}
                <ConditionalRender if={props.setFilterOptions}>
                    {props.setFilterOptions &&
                        <FilterBar setFilterOptions={props.setFilterOptions}/>}
                </ConditionalRender>

                <Routes>
                    <Route
                            path="/text"
                            element={<TextAndPlan
                                    setExecutionPlanOptions={setExecutionPlanOptions}
                                    batchId={batchId}
                                    batchStatementId={batchStatementId}
                                    instance={instance}
                                    statementId={statementId}
                            />}
                    />

                    <Route
                            path="/activity"
                            element={<StatementActivity eventTypes={props.eventTypes}
                                                        instance={instance}
                                                        batchId={batchId}
                                                        applyPeriod={props.applyPeriod}
                                                        batchStatementId={batchStatementId}
                                                        chartPlanPlotLines={chartPlanPlotLines}
                                                        statementId={statementId}
                                                        setFilterOptions={props.setFilterOptions}
                                    // chartPlanPlotLines={this.state.chartPlanPlotLines}
                                    // statementTimePrevious={this.state.statementTimePrevious}/>}
                            />}
                    />

                    <Route path="/sql-statistics"
                           element={<SqlStatistics instance={instance}
                                                   filterParameters={filterParameters}
                                                   applyPeriod={props.applyPeriod}
                                                   batchStatementId={batchStatementId}
                                                   statementId={statementId || '0'}
                           />}/>

                    <Route
                            path='/events'
                            element={
                                <Events
                                        instance={instance}
                                        applyPeriod={props.applyPeriod}
                                        batchStatementId={batchStatementId}
                                        statementId={statementId || "0"}
                                />
                            }
                    />

                    <Route path="/batches"
                           element={<Batches filtersParameters={filterParameters}
                                             instanceId={instance.id}
                                             statementId={statementId} showByDefault={true}
                                             isBatchesForStatement={true}
                           />}/>

                </Routes>
            </div>
    )
}

SQLStatisticStatement.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
    applyPeriod: PropTypes.func.isRequired,
    setFilterOptions: PropTypes.func.isRequired
};

export default SQLStatisticStatement;